/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {AppSettings} from '../models/Constants';
import {AuthenticationErrorHandlerService} from '../services/authentication/authentication-error-handler.service';
import {AUTHENTICATION_PROVIDER} from '../services/authentication/authentication-provider.token';
import {FeatureFlaggingService} from '../services/feature-flagging.service';
import {
  iconAdd,
  iconAddCircle,
  iconAnalyze,
  iconArrowDownRight,
  iconAssetNetwork,
  iconBatteryEmpty,
  iconBatteryFull,
  iconBatteryThreeQuarter,
  iconCalendar,
  iconCheckbox,
  iconCheckboxEmpty,
  iconChevronDownSmall,
  iconChevronRightSmall,
  iconClear,
  iconCloudNew,
  iconConnected,
  iconContextMenu,
  iconCycle,
  iconDataTypeEnum,
  iconDataTypeInteger,
  iconDataTypeString,
  iconDiagramModule,
  iconDocumentLink,
  iconDoughnutchartFilled,
  iconEMail,
  iconExport,
  iconFactoryReset,
  iconGauge,
  iconGlobe,
  iconHistory,
  iconHome,
  iconImage,
  iconImport,
  iconInfo,
  iconLabel,
  iconLanguage,
  iconLayers,
  iconLeaf,
  iconList,
  iconMoreMenu,
  iconOpenExternal,
  iconPen,
  iconPlus,
  iconProduct,
  iconQrCode,
  iconQuestion,
  iconRefresh,
  iconRemoveEye,
  iconRename,
  iconScale,
  iconScript,
  iconSearch,
  iconTrashcan,
  iconUserManagement,
  iconUserProfile,
} from '@siemens/ix-icons/icons';
import {addIcons} from '@siemens/ix-icons';
import {UserInactivityService} from '../services/user-inactivity.service';
import {UserService} from '../services/user.service';
import {FeatureFlag} from '../models/FeatureFlag';
import {ThemeService} from '../services/theme.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly featureFlaggingService = inject(FeatureFlaggingService);
  private readonly auth = inject(AUTHENTICATION_PROVIDER);
  private readonly authenticationErrorHandler = inject(
    AuthenticationErrorHandlerService,
  );
  private readonly userInactivityService = inject(UserInactivityService);
  private readonly userService = inject(UserService);
  private readonly themeService = inject(ThemeService);

  title = 'Siemens Digital Product Passports';

  constructor() {
    this.translate.setDefaultLang(AppSettings.DEFAULT_LANGUAGE);

    addIcons({
      iconQuestion,
      iconRename,
      iconImport,
      iconTrashcan,
      iconArrowDownRight,
      iconUserManagement,
      iconAddCircle,
      iconProduct,
      iconEMail,
      iconConnected,
      iconMoreMenu,
      iconCloudNew,
      iconHome,
      iconPen,
      iconImage,
      iconPlus,
      iconGlobe,
      iconRemoveEye,
      iconBatteryThreeQuarter,
      iconCheckbox,
      iconCheckboxEmpty,
      iconChevronDownSmall,
      iconOpenExternal,
      iconInfo,
      iconFactoryReset,
      iconLeaf,
      iconScript,
      iconBatteryFull,
      iconClear,
      iconSearch,
      iconChevronRightSmall,
      iconContextMenu,
      iconAdd,
      iconQrCode,
      iconDataTypeString,
      iconCalendar,
      iconDataTypeInteger,
      iconUserProfile,
      iconGauge,
      iconLayers,
      iconCycle,
      iconBatteryEmpty,
      iconDiagramModule,
      iconHistory,
      iconExport,
      iconLabel,
      iconAnalyze,
      iconDoughnutchartFilled,
      iconDocumentLink,
      iconLanguage,
      iconList,
      iconDataTypeEnum,
      iconScale,
      iconAssetNetwork,
      iconRefresh,
    });
  }

  ngOnInit(): void {
    this.setLanguage();
    // Reacts at application level to authentication errors published by the
    // selected provider (restart login or open the error page).
    this.authenticationErrorHandler.initialize();
    let features: FeatureFlag;
    this.auth.isAuthenticated$.subscribe(async (isAuthenticated) => {
      if (isAuthenticated) {
        features = await this.featureFlaggingService.load();
        this.themeService.setTheme(features);
      }
    });

    this.userInactivityService.startMonitoring(() => this.onUserInactivity());
  }

  onUserInactivity() {
    // prevent loop of logout if user already is on login page
    if (!globalThis.location.pathname.startsWith('/login')) {
      this.userService.logout('inactivityLogout');
    }
  }

  ngOnDestroy(): void {
    this.userInactivityService.stopMonitoring();
  }

  setLanguage() {
    const language = sessionStorage.getItem('language');
    if (language) {
      this.translate.use(language);
    } else {
      sessionStorage.setItem('language', AppSettings.DEFAULT_LANGUAGE);
      this.translate.use(AppSettings.DEFAULT_LANGUAGE);
    }
  }
}
